import Promise from 'bluebird';
import _intersection from 'lodash/intersection';
import { GEO_LOCATION_KEY, SUB_KEY, SESSION_TOKEN_KEY, USER_TRACKING_KEY, clientRenderRoutes } from 'constants';
import { Api, SourceType, ContentType, ContentStatus, URLPath, BucketLayout, Flags } from 'enums';
import { getEventByQuery } from 'common/actions/listingsActions';
import {
    createSDKWrapper,
    analyticsReady,
    streamSelect
} from 'common/actions/appActions';
import { initAdsConfig } from 'common/ads/actions';
import {
    checkSWIDAuthentication,
    checkOneIDAuthentication,
    completeOneIDAuthentication
} from 'common/actions/oneIdActions';
import {
    SearchAPI
} from 'lib/api';
import * as perf from 'lib/util/perf';
import { get, remove } from 'lib/util/localStorage';
import bootstrapSelector from 'common/selectors/bootstrapSelector';
import appSelector, { isIntlSelector, apiEnvSelector } from 'common/selectors/appSelector';
import oneIdSelector, { oneIdEntitlementsSelector } from 'common/selectors/oneIdSelector';
import routingSelector from 'common/selectors/routingSelector';
import { scoreAll } from 'common/actions/scoringActions';
import {
    graphql,
    getPage,
    getEvent,
    getVideo,
    getBucketPage,
    getPageWithPathId,
    getPageByFullUrl,
    addContentToStream,
    fetchPickerStreams
} from 'common/actions/apiAction';
import {
    createSmartAdobePassInitializeAction,
    createSmartIpAuthenticationAction,
    createGetBAMAuthenticationInfoAction,
    createGetGeoLocationAction,
    createGetBAMSessionTokenAction,
    createGetUserTrackingAction
} from 'common/actions/authenticationActions';
import { fetchScheduleModule } from 'common/scheduleModule/actions';
import { createSmartFanAction } from 'common/actions/fanActions';
import pageSelector, { isFinalSelector, showPromoSelector } from 'common/selectors/pageSelector';
import { hasTDA } from 'lib/auth';
import {
    superBundleSelector,
    bamSubReadySelector
} from 'common/selectors/authenticationSelectors';

const { Page: { EVENT_QUERY_KEYS, PCC_NETWORK_NAMES } } = Api;

export const CLEAR_PAGE = 'CLEAR_PAGE';
export const REQUEST_PAGE = 'REQUEST_PAGE';
export const RECEIVE_PAGE = 'RECEIVE_PAGE';
export const INVALIDATE_PAGE = 'INVALIDATE_PAGE';
export const LAUNCH_PLAYER = 'LAUNCH_PLAYER';
export const PAGE_INFO_SET = 'PAGE_INFO_SET';
export const COMPLETE_PAGE_LOAD = 'COMPLETE_PAGE_LOAD';
export const REFRESH_BUCKETS = 'REFRESH_BUCKETS';
export const REPLACE_BUCKETS = 'REPLACE_BUCKETS';
export const REPLACE_HERO = 'REPLACE_HERO';
export const SET_STATE = 'SET_STATE';
export const START_GET_COMPETITORS = 'START_GET_COMPETITORS';
export const COMPLETE_GET_COMPETITORS = 'COMPLETE_GET_COMPETITORS';

export function clearPage() {
    return {
        type: CLEAR_PAGE
    };
}

export function createRequestPageAction(id) {
    return {
        type: REQUEST_PAGE,
        id,
        requestedTime: Date.now()
    };
}

export function createReceivePageAction({
    id,
    name,
    description,
    header,
    image,
    icon,
    edition,
    buckets,
    tracking,
    heroContent,
    carouselBucket,
    lang,
    error,
    isFinal,
    noHero
}) {
    return {
        type: RECEIVE_PAGE,
        id,
        name,
        description,
        header,
        image,
        icon,
        edition,
        buckets,
        tracking,
        heroContent,
        carouselBucket,
        lang,
        error,
        isFinal,
        noHero,
        receivedTime: Date.now()
    };
}

export function startGetCompetitors() {
    return {
        type: START_GET_COMPETITORS,
        startTime: Date.now()
    };
}

export function completeGetCompetitors(competitors) {
    return {
        type: COMPLETE_GET_COMPETITORS,
        completeTime: Date.now(),
        competitors
    };
}


export function fetchAllCompetitors() {
    return (dispatch, getState) => {
        const state = getState();
        const apiEnv = apiEnvSelector(state);
        dispatch(startGetCompetitors());
        return SearchAPI.getAllCompetitors(apiEnv).then((competitors) => {
            dispatch(completeGetCompetitors(competitors));
            return competitors;
        });
    };
}

export function refreshBuckets(pageName, { params = {}, forceRefresh = false } = {}) {
    return async (dispatch, getState) => {
        const state = getState();
        const { id: currentPageId } = pageSelector(getState());
        let refreshedBuckets;

        try {
            // NOTE: Handles routes such as `product/ESPN_PLUS_MLB` that contain an `ID` in the path
            if (params.id) {
                const { id, seasonId } = params;
                let pathParams = id;
                if (seasonId) {
                    pathParams += `/seasons/${seasonId}`;
                }

                /**
                 * pageName override calling collection and show page in the pageApi returns 404
                 */
                let newPageName = pageName;
                if (pageName === 'collection') {
                    newPageName = 'bucket';
                } else if (pageName === 'show') {
                    newPageName = 'series';
                }
                const { page: { buckets } = {} } = await dispatch(getPageWithPathId({
                    name: newPageName,
                    pathParams
                }));
                refreshedBuckets = buckets;
            } else {
                const data = await dispatch(getPage({ name: pageName }));
                if (data && data.page) {
                    const { buckets = {} } = data.page;
                    refreshedBuckets = buckets;
                }
            }

            const { id: pageId } = pageSelector(state);
            if (pageId !== currentPageId) {
                return;
            }

            // Replace all the buckets
            if (forceRefresh) {
                dispatch({
                    type: REPLACE_BUCKETS,
                    buckets: refreshedBuckets
                });
            }

            dispatch({
                type: REFRESH_BUCKETS,
                buckets: refreshedBuckets
            });
        } catch (error) {
            console.error(`refreshBuckets() Error: Unable to refresh buckets for page: ${pageName}`, error);
            throw error;
        }
    };
}

export function completePageLoad(buckets) {
    return {
        type: COMPLETE_PAGE_LOAD,
        startTime: Date.now(),
        buckets
    };
}

// Checks if the contents of bucketPage all exist in one of the buckets and the bucket names match.
// We then consider them equal.
export function indexOfBucketInBucketList(buckets = [], bucket) {
    const contentIds = bucket.contents.map((content) => content.id);

    if (!buckets.length > 0) {
        return -1;
    }

    let foundIndex = -1;
    for (let index = 0; index < buckets.length; index += 1) {
        const bucketItem = buckets[index];
        const bucketItemContentIds = bucketItem.contents.map((content) => content.id);
        const isEqual = (bucketItem.name === bucket.name) && contentIds.every((id) => bucketItemContentIds.indexOf(id) !== -1);
        if (isEqual) {
            foundIndex = index;
            break;
        }
    }

    return foundIndex;
}

export function fetchPageData(pageName, params, query) {
    return (dispatch) => {
        if (pageName === 'collection') {
            const { bucketId } = query;
            return dispatch(getBucketPage({
                bucketId: params.id || bucketId,
                groupId: params.groupId,
                ignores: 'displayLimit'
            }));
        } else if (['film', 'product', 'show', 'program', 'compilation'].indexOf(pageName) !== -1) {
            const { id, seasonId } = params;
            const name = URLPath[pageName];
            let pathParams = id;
            if (seasonId) {
                pathParams += `/seasons/${seasonId}`;
            }
            return dispatch(getPageWithPathId({
                name,
                pathParams
            }));
        } else if (pageName && params.id !== '') {
            // NOTE: Handles `/catalog/:guid` without strict matching
            // TODO: This will eventually replace some pageName conditions in the `else if` above
            // since we will be deprecating `show`, `film` pages and use `catalog`
            return dispatch(getPageWithPathId({
                name: pageName,
                pathParams: params.id
            }));
        }

        return dispatch(getPage({ name: pageName }));
    };
}

export function fetchEventData(query) {
    return (dispatch) => {
        const { channel, network: networkQuery } = query;
        const network = networkQuery || channel;

        if (network) {
            query.network = network;
        }

        const hasInvalidNetwork = network && PCC_NETWORK_NAMES.indexOf(network) === -1;
        if (_intersection(Object.keys(query), EVENT_QUERY_KEYS).length > 0 && !hasInvalidNetwork) {
            return dispatch(getEvent({ query }));
        }

        return Promise.resolve();
    };
}

export function fetchVideoData({ id }) {
    return (dispatch) => dispatch(getVideo({ pathParams: id }));
}

export function fetchEventOrVideoByQuery(query) {
    return async (dispatch) => {
        let data;
        try {
            data = await dispatch(fetchEventData(query));
        } catch (err) {
            console.error(err);
        }

        if (!data) { // no valid query param found
            return data;
        }

        if (!data.page.contents && query.id) { // most likely it's a clip
            try {
                data = await dispatch(fetchVideoData(query));
            } catch (err) {
                console.error(err);
            }
        }

        return data;
    };
}

export function fetchBuckets(query, fullUrl) {
    return (dispatch) => {
        const { bucketId } = query;

        if (fullUrl) {
            return dispatch(getPageByFullUrl({ url: fullUrl }));
        } else if (bucketId) {
            return dispatch(getBucketPage({
                bucketId
            }));
        }

        return Promise.resolve();
    };
}

export async function handlePickerStreams(dispatch, pickerApiUrl, eventId) {
    /**
     * if pickerApiUrl returns from above event pageApi, fetch picker pageApi
     * and update picker redux state
     */
    let pickerStream;
    if (pickerApiUrl) {
        pickerStream = await dispatch(fetchPickerStreams(pickerApiUrl, eventId));
    }
    return pickerStream;
}

export function createFetchPageAction(id, params, query, lang, fullUrl) {
    perf.markStart('createFetchPageAction');
    return async (dispatch, getState) => {
        const pageName = id;
        const state = getState();
        const isIntl = isIntlSelector(state);
        const isFinal = isFinalSelector(state);
        const { id: currentId } = pageSelector(state);

        let noHero = showPromoSelector(state) && id === 'espnplus';

        const shouldRefreshAds = id !== currentId;
        dispatch(createRequestPageAction(id));

        let pageData = dispatch(fetchPageData(id, params, query));
        let heroData = dispatch(fetchEventOrVideoByQuery(query));

        let bucketsData = pageName === 'player' ? dispatch(fetchBuckets(query, fullUrl)) : Promise.resolve();
        try {
            [pageData, heroData, bucketsData] = [await pageData, await heroData, await bucketsData];
        } catch (err) {
            console.error(err);
        }
        // even if deep linked with single `id`, if the feed is part of an event, the player should show feeds picker,
        // so check if it's part of an event, and fetch all streams with `eventId` if so.
        let eventStreams = null;
        let isHandlePickerCalled = false;
        if (
            !isIntl &&
            query.id && !query.eventId && !query.gameId &&
            heroData && heroData.page && heroData.page.contents && heroData.page.contents.type === ContentType.LISTING) {
            const { id: contentId, isEvent, eventId, status } = heroData.page.contents;
            if (isEvent || eventId) {
                /**
                 * following try catch block code will be called after calling event pageApi with an airingId,
                 * ex: watch/?id=9de24e02-1e11-43aa-9590-e12cf35a492a which will trigger event pageApi with an airingId.
                 * When the response returns eventId, event pageApi will call again with eventId to fetch event data
                 */
                let pickerApiUrl;
                try {
                    const eventHeroData = await dispatch(fetchEventData({
                        eventId: eventId || contentId,
                        status: status.toUpperCase()
                    }));
                    const contents = eventHeroData && eventHeroData.page && eventHeroData.page.contents;
                    if (contents && contents.streams && contents.streams.length > 0) {
                        const { links = {} } = contents;
                        pickerApiUrl = links.picker;
                        eventStreams = contents.streams;
                    }
                } catch (err) {
                    console.error(err);
                }

                // it is an event, we need to handle picker streams logic
                isHandlePickerCalled = await handlePickerStreams(dispatch, pickerApiUrl, eventId);
            }
        }

        // getPage should never be rejected, it resolves with an error page skeleton, so no need for isFulfilled().
        const {
            page: {
                name,
                description,
                header,
                image,
                icon,
                edition = {},
                buckets: originalBuckets = [],
                tracking,
                error
            } = {}
        } = pageData || {};

        // bluebird seems to return a reference to originally cached pageapi request to player
        // here's a trick to operate on a new reference
        const buckets = originalBuckets.concat([]);

        if (bucketsData && bucketsData.page.buckets) {
            const newBucket = bucketsData.page.buckets[0];
            const newBucketIndex = indexOfBucketInBucketList(buckets, newBucket);
            const isBucketInBucketList = newBucketIndex !== -1;

            if (isBucketInBucketList && newBucketIndex !== 0) {
                buckets[newBucketIndex].__replaced = true;
            } else if (!isBucketInBucketList) {
                const index = buckets.map(
                    (bucket) => (bucket.tags && bucket.tags.indexOf('replaceable') !== -1)
                ).indexOf(true);
                if (index !== -1) {
                    buckets[index] = newBucket;
                    buckets[index].__replaced = true;
                }
            }
        }

        let heroContent = null;

        if (heroData && heroData.page && heroData.page.contents) {
            heroContent = heroData.page.contents;
            if (eventStreams && eventStreams.length > 0) {
                heroContent.streams = eventStreams;
            }
            // if the hero was determined via id param, mark as concrete as to not change when scored
            heroContent.concrete = true;
        }

        // if there is a bucket which type is `BucketLayout.Carousel`,
        // it should be rendered as a carousel component and featured on top (used in `Originals` page)
        let carouselBucket;
        buckets.some(({ layout }, index) => {
            if (layout && layout.toLowerCase() === BucketLayout.Carousel) {
                carouselBucket = buckets.splice(index, 1)[0];
                return true;
            }
            return false;
        });

        if (carouselBucket) {
            noHero = true;
        }

        // If there is no specified hero content, grab the first content in the first bucket as hero
        if (!heroContent && !noHero) {
            let content;
            if (header) { // film or show
                if (header.bucket && header.bucket.contents && header.bucket.contents[0]) {
                    content = header.bucket.contents[0];
                    // workaround to remove duplicated live content in the first bucket
                    if (buckets && buckets[0] && buckets[0].contents &&
                        buckets[0].contents.length === 1 &&
                        content.status === ContentStatus.LIVE &&
                        buckets[0].contents[0].id === content.id
                    ) {
                        buckets.shift();
                    }
                } else {
                    noHero = true;
                }
            } else if (buckets && buckets[0] && buckets[0].contents && buckets[0].contents[0]) {
                content = buckets[0].contents[0];
            }
            if (content && (content.type === ContentType.LISTING || content.type === ContentType.VOD ||
                content.type === ContentType.EVENT || content.type === ContentType.VIDEO)) {
                /**
                 * Ticket: https://espnjira.disney.com/browse/WEWEB-2501
                 * Fixed missing content props that we need to pass to DSS from hero component
                 * */
                if (content.streams.length && !content.streams[0].content) {
                    content = addContentToStream(content);
                }

                heroContent = content;
                // NOTE: The following logic catches the case when there is no Hero content
                // and we select the first item in the buckets array as a fallback for the Hero content
                // If that first bucket contains an `eventId` or `isEvent` property is truthy,
                // we fetch the picker streams using the first item data
                if (((heroContent.eventId || heroContent.isEvent) && !query.eventCalendarId) && !isHandlePickerCalled) {
                    const { links: { picker: pickerUrl } = {} } = heroContent;
                    await handlePickerStreams(dispatch, pickerUrl, heroContent.eventId);
                }
            }
        }

        dispatch(createReceivePageAction({
            id,
            name,
            description,
            header,
            image,
            icon,
            edition,
            buckets,
            tracking,
            heroContent,
            carouselBucket,
            lang,
            error,
            isFinal,
            noHero
        }));

        if (shouldRefreshAds && pageName !== 'player') { // player page sets ads config when a content is selected
            dispatch(initAdsConfig(pageName, tracking, {}, query));
        }

        perf.measureAndTime('createFetchPageAction');
        return Promise.resolve();
    };
}

export function oneIdAndBam(bamProxyIFrameId, requests) {
    return (dispatch) => (
        dispatch(checkOneIDAuthentication())
            .then(() => dispatch(createSDKWrapper(bamProxyIFrameId, requests)))
    );
}

export function createPageShellInitAction(params, bamProxyIFrameId) {
    return async (dispatch, getState) => {
        perf.markStart('createPageShellInitAction');
        const state = getState();
        const isIntl = isIntlSelector(state);

        if (isIntl) {
            dispatch(createSmartAdobePassInitializeAction(params));
            dispatch(createSmartIpAuthenticationAction());
            dispatch(completeOneIDAuthentication(false, []));
        } else {
            const { pageApi: { queryParameters = [], requireClientRenderParams = [] } = {} } = bootstrapSelector(state);
            const { lang } = appSelector(state);
            const { locationBeforeTransitions } = routingSelector(state);
            const { id: pageId } = pageSelector(state);
            const { query = {} } = locationBeforeTransitions || {};
            const isLoggedIn = await dispatch(checkSWIDAuthentication());
            const { swid } = oneIdSelector(getState());
            const geo = get(GEO_LOCATION_KEY);
            const userTracking = get(USER_TRACKING_KEY);
            let sub = get(SUB_KEY);
            let sessionToken = get(SESSION_TOKEN_KEY);
            const requireClientRender = _intersection(queryParameters, requireClientRenderParams).length > 0;
            const requireGeo = requireClientRender && queryParameters.indexOf('zipcode') > -1 && !geo;
            const requireFan = (
                requireClientRender &&
                (queryParameters.indexOf('favoriteTeam') > -1 || queryParameters.indexOf('favoriteSport') > -1)
            );
            let requireCheckOneId = isLoggedIn;

            if (sub) {
                if (!isLoggedIn || !sub.swid || sub.swid !== swid) {
                    remove(SUB_KEY);
                    sub = null;
                } else {
                    dispatch(createGetBAMAuthenticationInfoAction(sub.subscriptions));
                    const newState = getState();
                    const entitlements = oneIdEntitlementsSelector(newState);
                    const hasSuperBundle = superBundleSelector(newState);
                    const bamSubReady = bamSubReadySelector(newState);
                    const hasTDAEntitlement = hasTDA(sessionToken);
                    const isAuthenticated = (entitlements && entitlements.length > 0) || hasTDAEntitlement;
                    dispatch(
                      completeOneIDAuthentication(isAuthenticated, entitlements, hasTDAEntitlement, hasSuperBundle, bamSubReady)
                    );
                    requireCheckOneId = false;
                }
            }

            if (geo) {
                dispatch(createGetGeoLocationAction(geo));
            }

            if (sessionToken) {
                if (!isLoggedIn) {
                    remove(SESSION_TOKEN_KEY);
                    sessionToken = null;
                } else {
                    dispatch(createGetBAMSessionTokenAction(sessionToken));
                }
            }

            if (userTracking) {
                dispatch(createGetUserTrackingAction(userTracking));
            }

            if (isLoggedIn) {
                dispatch(
                    oneIdAndBam(
                        bamProxyIFrameId, requireGeo ?
                            ['geolocation', 'subscriptions', 'sessionToken', 'userTrackingInformation'] :
                            ['subscriptions', 'sessionToken', 'userTrackingInformation']
                    )
                );
            } else {
                // if user is not logged, we need to trigger createSDKWrapper call to get user tracking info from DSS sdk
                // ticket: https://jira.disney.com/browse/WEWEB-2597
                dispatch(createSDKWrapper(bamProxyIFrameId, ['userTrackingInformation']));
                dispatch(completeOneIDAuthentication(false, []));
            }
            dispatch(createSmartAdobePassInitializeAction(params));
            // find out what actions are necessary to await to reflect to Page API params
            const requiredActions = [];
            if (requireGeo) { // grabbing zipcode from location data since BAM's geolocation uses IP info as well.
                requiredActions.push(dispatch(createSmartIpAuthenticationAction()));
            } else {
                dispatch(createSmartIpAuthenticationAction());
            }

            if (requireCheckOneId) {
                requiredActions.push(dispatch(checkOneIDAuthentication()));
            } else {
                dispatch(checkOneIDAuthentication());
            }

            if (requireFan) {
                requiredActions.push(dispatch(createSmartFanAction()));
            } // we don't need it now

            if (requiredActions.length) {
                try {
                    // make sure each action doesn't throw, otherwise it exits as soon as it throws
                    await Promise.all(requiredActions);
                } catch (err) {
                    console.error('createPageShellInitAction', err);
                }
            }

            const { id: currentPageId } = pageSelector(getState());
            if (requireClientRender || (query[Flags.NO_SSR] === 'true' && clientRenderRoutes.indexOf(pageId) > -1)) {
                // it's possible the pageShell actions above takes long and user might navigates
                // to other pages before the above actions complete, check the current page id
                // not to override the current page with the previous page content
                if (pageId && pageId === currentPageId && pageId !== 'schedule' && pageId !== 'search' && pageId !== 'sid') {
                    const actions = [dispatch(createFetchPageAction(pageId, params, query, lang))];
                    if (pageId !== 'player') {
                        actions.push(dispatch(fetchScheduleModule(lang)));
                    }
                    try {
                        await Promise.all(actions);
                    } catch (err) {
                        console.error('createPageShellInitAction', err);
                    }
                    perf.measureAndTime('createPageShellInitAction');
                }
            }
        }

        dispatch(scoreAll());
    };
}

export function createLaunchPlayerAction(content) {
    const id = content.id;
    const tileType = content.type || '';
    const source = content.sourceType === SourceType.LINEAR ? content.source : null;

    return {
        type: LAUNCH_PLAYER,
        id,
        source,
        tileType,
        launchedAt: Date.now()
    };
}

export function setPageInfo(id, name, autoPageViewTrack = true) {
    return (dispatch) => {
        dispatch({
            type: PAGE_INFO_SET,
            id,
            name,
            isFinal: true,
            autoPageViewTrack
        });
    };
}

export function replaceHero(content) {
    return {
        type: REPLACE_HERO,
        content
    };
}

export function setPageState(isFinal) {
    return {
        type: SET_STATE,
        isFinal
    };
}

export function getAiring({ id }) {
    return async (dispatch) => {
        try {
            const { data = {} } = await dispatch(graphql('getAiring', { id }));
            const { airing } = data;
            return airing;
        } catch (err) {
            return {};
        }
    };
}

export function getAirings({ eventId }) {
    return async (dispatch) => {
        try {
            const { data = {} } = await dispatch(graphql('getAirings', { eventId, limit: 500 }));
            const { airings } = data;
            return airings;
        } catch (err) {
            return [];
        }
    };
}

export function singleEventPage(id, name, query) {
    return async (dispatch) => {
        dispatch(createRequestPageAction(id));
        dispatch(analyticsReady({}));

        const eventId = query.eventId || query.gameId;
        const { partner, dates, thumbnail } = query;
        let content;
        if (!query.id && partner) {
            content = await dispatch(getEventByQuery({ partner, dates }));
        }

        let newQuery;
        delete query.thumbnail;
        if (content) {
            delete query.eventId;
            delete query.gameId;
            delete query.league;
            delete query.partner;
            delete query.dates;
            newQuery = { ...query, id: content.id };
        } else if (eventId) {
            newQuery = { eventId };
        } else if (query.id || query.channel || query.network) {
            newQuery = query;
        } else if (!partner) {
            newQuery = {
                ...query,
                network: 'espn'
            };
        }

        let payload;
        if (newQuery) {
            payload = await dispatch(fetchEventOrVideoByQuery(newQuery));
        }
        const { page: { edition, contents: heroContent } = {} } = payload || {};
        if (heroContent) {
            if (thumbnail) {
                heroContent.imageHref = thumbnail;
            }
            const { streams = [] } = heroContent;
            if (streams.length) {
                await dispatch(streamSelect(streams[0]));
            }
        }
        dispatch(createReceivePageAction({
            id,
            name,
            edition,
            heroContent,
            isFinal: true
        }));
    };
}



// WEBPACK FOOTER //
// ./app/common/page/actions/index.js